import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import Form from 'mobx-react-form';
import { get } from 'lodash';

import { Grid, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { RentRollTableColumn } from 'types/underwriting/income-approach';
import RentRollTable from './RentRollTable';

import { formOptions } from 'forms/utils/formOptions';
import { plugins } from 'forms/utils/formPlugins';
import { FormOptions, wrapForm } from 'components/shared/form';
import NumericInput from 'components/shared/inputs/NumericInput';
import { RENT_ROLL_LABELS } from '../../constants';
import { useRootStore } from 'stores/RootStore';
import { rentRollFormFields, rentRollFormHelpers } from './rentRollFormConfig';
import { getSubjectUnitGroups, groupCompsByUse } from '../helpers';
import { SummaryTable } from './SummaryTable';
import { divide } from 'shared/utils/numberOperations';
import { formatCurrencyFloat, formatPercentageString } from 'shared/utils/numberFormatters';
import { INCOME_APPROACH_BASIS_TYPES } from 'shared/calculations/incomeApproach';

const formConfig: FormOptions = {
  fields: rentRollFormFields,
  options: {
    ...formOptions,
    validateOnChange: true,
  },
  plugins,
};

type ProjectedCommercialRentRollProps = {
  form: Form;
};

const ProjectedCommercialRentRoll = observer(({ form }: ProjectedCommercialRentRollProps) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData } = underwritingPageStore;
  const property = get(underwritingData, 'property');
  const rentComps = get(underwritingData, 'incomeApproach.commercial.rentComps.comps', []);
  const isProjectedRentRoll = true;
  const columns: RentRollTableColumn[] = rentRollFormHelpers.getUnitsTableColumns(isProjectedRentRoll);

  const rentRollField = form.$('incomeApproach.commercial.rentRoll');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rentRoll = rentRollField.value || [];

  const commercialVCLossPercentageField = form.$('incomeApproach.commercial.vcLossPercentage');
  const commercialVCLossPercentage = commercialVCLossPercentageField.value;

  const borrowerTotal = rentRollFormHelpers.getCommercialIncomeTotal(form, INCOME_APPROACH_BASIS_TYPES.borrower);
  const borrowerLoss = useMemo(() => {
    return divide(borrowerTotal * commercialVCLossPercentage, 100);
  }, [borrowerTotal, commercialVCLossPercentage]);
  const effectiveBorrowerIncome = borrowerTotal - borrowerLoss;

  const underwriterTotal = useMemo(() => {
    return rentRollFormHelpers.getCommercialIncomeTotal(form, INCOME_APPROACH_BASIS_TYPES.projected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentRoll]);
  const underwriterLoss = useMemo(() => {
    return divide(underwriterTotal * commercialVCLossPercentage, 100);
  }, [underwriterTotal, commercialVCLossPercentage]);
  const effectiveUnderwriterIncome = underwriterTotal - underwriterLoss;

  const compGroups = groupCompsByUse(rentComps);
  const subjectUnitGroups = useMemo(() => {
    return getSubjectUnitGroups(rentRoll);
  }, [rentRoll]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{ marginTop: '16px', marginBottom: '16px' }}>
        <Grid item xs={12} sx={{ pb: 1 }}>
          <Typography variant="h6">Borrower & Comparables Summary</Typography>
        </Grid>
        <Grid item xs={12}>
          {compGroups.map((compGroup) => {
            return <SummaryTable compGroup={compGroup} subjectUnitGroups={subjectUnitGroups} />;
          })}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">Projected Commercial Rent Roll</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <NumericInput
              disabled
              size="small"
              value={property?.commercialUnits ?? 0}
              label={RENT_ROLL_LABELS.NUMBER_OF_COMMERCIAL_UNITS}
            />
          </Grid>
          <Grid item xs={12}>
            <RentRollTable form={form} columns={columns} isProjectedRentRoll={isProjectedRentRoll} />
          </Grid>

          <Grid item xs={12} sx={{ pt: 2 }}>
            <Typography variant="h6">Vacancy & Collection Loss</Typography>
            <Grid item xs={2}>
              <NumericInput
                {...commercialVCLossPercentageField.bind()}
                size="small"
                margin="normal"
                fullWidth
                label="Commercial VC Loss %"
                decimalScale={2}
                max={100}
                allowNegative={false}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid xs={6}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell align="right">Borrower</TableCell>
                    <TableCell align="right">Projected</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pl: 2 }}>Potential Commercial Income</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(borrowerTotal)}</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(underwriterTotal)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 2 }}>
                      Less Commercial V/C Loss @ {formatPercentageString(commercialVCLossPercentage)}
                    </TableCell>
                    <TableCell align="right">{formatCurrencyFloat(-1 * borrowerLoss)}</TableCell>
                    <TableCell align="right">{formatCurrencyFloat(-1 * underwriterLoss)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Effective Commercial Income</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      {formatCurrencyFloat(effectiveBorrowerIncome)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      {formatCurrencyFloat(effectiveUnderwriterIncome)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default wrapForm({
  ...formConfig,
})(ProjectedCommercialRentRoll);
